import React, { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { MemoryHistory } from 'history';

import classNames from 'classnames';

import { Focusable } from 'components/focus';
import { useFocusManager } from 'hooks/focus';
import FocusGroup from 'components/focus/FocusGroup';
import Selectable from 'components/focus/Selectable';

import LogoPage from 'components/pages/LogoPage';
import WithLoading from 'components/common/WithLoading';

import useTracking from 'hooks/analytics';
import { getPageValues } from 'support/analytics/analyticsHelper';

import applicationActions from 'common/support/applicationActions';

import { VideoCtvRedux } from 'store-types';
import Button from './PrivacyButton';
import styles from './PrivacyPageUS.module.scss';

interface CmpUserInterfaceData {
  multiParagraphDescription: string[];
  title: string;
  primaryButtonText: string;
  secondaryButtonText: string;
}

interface CmpConsentState {
  [key: string]: boolean;
}

interface CmpPrivacyUS {
  addEventListener: (event: string, callback: () => void) => void;
  consent: {
    getState: () => CmpConsentState;
    acceptAll: () => void;
    rejectAll: () => void;
  };
  userInterface: {
    getState: () => CmpUserInterfaceData;
  };
}

interface ParamountCmpUS {
  PrivacyEvent: {
    CONSENT_READY: string;
    USER_INTERFACE_READY: string;
  };
}

declare const window: Window & {
  cmp: CmpPrivacyUS;
  ParamountCmp: ParamountCmpUS;
};

interface TrackingPageData {
  pageType: string;
  [key: string]: unknown;
}

const trackPrivacyPageUS = (tracker: (data: TrackingPageData) => void): void => {
  const pageData: TrackingPageData = {
    pageType: 'privacy',
    ...getPageValues('privacy'),
  };

  tracker(pageData);
};

const PrivacyPageUS: React.FC = () => {
  const history = useHistory() as MemoryHistory;
  const focusManager = useFocusManager();
  const ksLoading = useSelector((state: VideoCtvRedux) => state.kitchenSink.loading);

  const scrollableDivRef = useRef<(HTMLDivElement & { hasFocus?: boolean })|null>(null);

  useEffect(() => {
    focusManager.setCurrentFocus('privacy-scrollBox');
  }, [focusManager]);

  let userConsent = true;

  window.cmp.addEventListener(
    window.ParamountCmp.PrivacyEvent.CONSENT_READY,
    () => {
      const consent = window.cmp.consent.getState();
      const consentArray = Object.values(consent);

      for (const status of consentArray) {
        if (!status) {
          userConsent = false;
          break;
        } else {
          userConsent = true;
        }
      }
    },
  );

  let cmpUserInterfaceData: CmpUserInterfaceData | undefined;

  window.cmp.addEventListener(
    window.ParamountCmp.PrivacyEvent.USER_INTERFACE_READY,
    () => {
      // get ParamountCmp UI data
      cmpUserInterfaceData = window.cmp.userInterface.getState();
    },
  );

  const paragraphText = cmpUserInterfaceData?.multiParagraphDescription;
  const checkBoxText = cmpUserInterfaceData?.title;
  const saveButtonText = cmpUserInterfaceData?.primaryButtonText;
  const backButtonText = cmpUserInterfaceData?.secondaryButtonText;

  const [isChecked, setIsChecked] = useState(userConsent);

  const scrollDown = (action: string): void => {
    let scrollBy = 0;
    if (action === 'moveDown') {
      scrollBy = 100;
    } else if (action === 'moveUp') {
      scrollBy = -100;
    } else {
      return;
    }
    scrollableDivRef.current?.scrollBy({ top: scrollBy });
  };

  const onAction = (action: string): boolean => {
    if (focusManager.currentFocusId === 'privacy-scrollBoxText') {
      if (action === applicationActions.back) {
        focusManager.setCurrentFocus('privacy-scrollBox');
      }
      return true;
    }

    if (
      action === applicationActions.back ||
      action === applicationActions.moveRight
    ) {
      focusManager.setCurrentFocus('privacy-back-button');
      return true;
    }

    return false;
  };

  const onActionBack = (action: string): boolean => {
    if (action === applicationActions.back) {
      return true;
    }

    if (
      action === applicationActions.moveLeft &&
      focusManager.currentFocusId === 'privacy-back-button'
    ) {
      focusManager.setCurrentFocus('privacy-scrollBox');
      return true;
    }

    return false;
  };

  const onClickText = (): void => {
    focusManager.setCurrentFocus('privacy-scrollBoxText');
  };

  const onClickCheckBox = (): void => {
    setIsChecked(!isChecked);
  };

  const onClickSave = (): void => {
    if (isChecked) {
      window.cmp.consent.acceptAll();
    } else {
      window.cmp.consent.rejectAll();
    }
    history.goBack();
  };

  const onClickBack = (): void => {
    history.goBack();
  };

  const getFocusClassCheckBox = (hasFocus: boolean): string => {
    let focusClass: string;

    if (hasFocus) {
      focusClass = isChecked ? styles.checkFocused : styles.checkTransparent;
    } else {
      focusClass = isChecked ? styles.checkUnFocused : styles.checkTransparent;
    }

    return focusClass;
  };

  const { trackPage: tracker } = useTracking();
  useEffect(() => {
    trackPrivacyPageUS(tracker);
  }, [tracker]);

  return (
    <Focusable
      focusId="privacy-page"
      className={classNames('w-100 h-100 fixed-top', styles.page)}
      data-testid="privacy-page"
      render={() => (
        <WithLoading loading={ksLoading}>
          <LogoPage
            className={classNames(
              'd-flex flex-row align-items-top position-relative',
              styles.logoPage,
            )}
          >
            <FocusGroup
              className={classNames(
                'd-flex',
                'flex-column',
                'align-items-center',
                'justify-content-between',
                styles.contentContainer,
              )}
              onActionOverride={onAction}
            >
              <Selectable
                focusId="privacy-scrollBox"
                className={styles.scrollBoxContainer}
                ttsText="Privacy Policy"
                onSelect={onClickText}
                render={({ hasFocus }: { hasFocus: boolean }) => (
                  <div
                    className={classNames(
                      styles.boxBorder,
                      hasFocus ? styles.focused : styles.unfocusedNoBorder,
                    )}
                  >
                    <Focusable
                      focusId="privacy-scrollBoxText"
                      className={classNames(
                        styles.scrollBoxText,
                        scrollableDivRef.current?.hasFocus
                          ? styles.focusedScrollBar
                          : '',
                      )}
                      data-testid="privacy-scrollBoxText"
                      ttsText={paragraphText}
                      render={() => (paragraphText||[]).join('\r\n\n')}
                      onAction={scrollDown}
                      ref={scrollableDivRef}
                      onFocusSet={(value: boolean) => {
                        if (value) {
                          scrollableDivRef.current?.classList.add(
                            styles.focusedScrollBar,
                          );
                        } else {
                          scrollableDivRef.current?.classList.remove(
                            styles.focusedScrollBar,
                          );
                        }
                      }}
                    />
                  </div>
                )}
              />
              <div
                className={classNames(
                  'd-flex flex-row align-items-center',
                  styles.checkBoxContainer,
                )}
              >
                <div className={classNames(styles.checkBoxText)}>
                  {checkBoxText}
                </div>
                <Selectable
                  focusId="privacy-checkbox"
                  className={styles.checkBoxInput}
                  ttsText={checkBoxText}
                  onSelect={onClickCheckBox}
                  render={({ hasFocus }: { hasFocus: boolean }) => (
                    <div
                      className={classNames(
                        styles.boxBorder,
                        hasFocus ? styles.focused : styles.unfocusedBorder,
                      )}
                    >
                      <input
                        className={classNames(
                          styles.checkBox,
                          getFocusClassCheckBox(hasFocus),
                        )}
                        type="checkbox"
                        checked={isChecked} // Binds the checkbox's checked state to the isChecked state variable
                      />
                    </div>
                  )}
                />
              </div>
              <Button
                type="button"
                focusId="privacy-save-button"
                className={styles.saveButton}
                ttsText={saveButtonText as string}
                onClick={onClickSave}
              >
                {saveButtonText}
              </Button>
            </FocusGroup>
            <FocusGroup
              className={styles.backButtonContainer}
              onActionOverride={onActionBack}
              horizontal
            >
              <Button
                type="button"
                focusId="privacy-back-button"
                className={styles.backButton}
                ttsText={backButtonText as string}
                onClick={onClickBack}
              >
                {backButtonText}
              </Button>
            </FocusGroup>
          </LogoPage>
        </WithLoading>
      )}
    />
  );
};

export default PrivacyPageUS;
